"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { getAccessToken } from "@/lib/auth/client";
import { isRecentlyExpired } from "@/lib/utils";
import type { MembershipSummary, MembershipMemberCard } from "@/lib/api/types";

interface MembershipContextValue {
  memberships: MembershipSummary[];
  memberCards: MembershipMemberCard[];
  selectedMembershipId: string;
  setSelectedMembershipId: (id: string) => void;
  isLoaded: boolean;
  /** True when the user has any membership record (from either endpoint). */
  hasMembership: boolean;
  /** True when the user has an active (non-expired) membership. */
  isActiveMember: boolean;
  /** True when the user's membership is expired but within the 60-day grace window. */
  isRecentlyExpiredMember: boolean;
  /** The expiration date string of the selected (or first) membership. */
  expirationDate: string | undefined;
  /** Maps altruLevelId → guest pass allocation count for all membership levels. */
  allocationByLevelId: Record<string, number>;
  /** Refresh membership and card data from the API. */
  refresh: () => void;
}

const MembershipContext = createContext<MembershipContextValue | null>(null);

/**
 * @component MembershipProvider
 * @description Fetches /api/membership and /api/membership/cards once at the
 * dashboard layout level and makes the data available to any descendant via
 * useMembership(). Derives membership status flags so consumers don't repeat logic.
 */
export function MembershipProvider({ children }: { children: ReactNode }) {
  const [memberships, setMemberships] = useState<MembershipSummary[]>([]);
  const [memberCards, setMemberCards] = useState<MembershipMemberCard[]>([]);
  const [selectedMembershipId, setSelectedMembershipId] = useState("");
  const [allocationByLevelId, setAllocationByLevelId] = useState<Record<string, number>>({});
  const [isLoaded, setIsLoaded] = useState(() => !getAccessToken());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch("/api/membership", { headers }).then((r) => (r.ok ? r.json() : null)),
      fetch("/api/membership/cards", { headers }).then((r) => (r.ok ? r.json() : null)),
      fetch("/api/membership/guest-pass-allocations", { headers }).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([membershipRes, cardsRes, allocationsRes]) => {
        const membershipData: MembershipSummary[] =
          membershipRes?.success && Array.isArray(membershipRes.data)
            ? membershipRes.data
            : [];
        const cardsData: MembershipMemberCard[] =
          cardsRes?.success && Array.isArray(cardsRes.data) ? cardsRes.data : [];
        const allocationsData: Record<string, number> =
          allocationsRes?.success && typeof allocationsRes.data === "object"
            ? allocationsRes.data
            : {};

        setMemberships(membershipData);
        setMemberCards(cardsData);
        setAllocationByLevelId(allocationsData);
        if (membershipData.length > 0) {
          // On first load (no selection yet) or if the current selection was removed,
          // default to the first membership. Preserve the selection on subsequent refreshes.
          setSelectedMembershipId((prev) => {
            const stillExists = membershipData.some((m) => m.membershipId === prev);
            return stillExists ? prev : membershipData[0].membershipId;
          });
        }
      })
      .catch(() => {})
      .finally(() => setIsLoaded(true));
  }, [tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // Derived status — computed once here, consumed everywhere
  const selectedMembership =
    memberships.find((m) => m.membershipId === selectedMembershipId) ?? memberships[0] ?? null;
  const primaryCard = memberCards.find((c) => c.isPrimary) ?? memberCards[0] ?? null;
  const expirationDate =
    selectedMembership?.expirationDate ?? primaryCard?.expirationDate ?? undefined;

  const hasMembership = memberships.length > 0 || memberCards.length > 0;

  const rawStatus =
    selectedMembership?.status ??
    (primaryCard?.membershipStatus ?? "");
  const isActiveMember = hasMembership && rawStatus.toLowerCase() === "active";
  const isExpired = hasMembership && rawStatus.toLowerCase() === "expired";
  const isRecentlyExpiredMember = isExpired && isRecentlyExpired(expirationDate);

  return (
    <MembershipContext.Provider
      value={{
        memberships,
        memberCards,
        selectedMembershipId,
        setSelectedMembershipId,
        isLoaded,
        hasMembership,
        isActiveMember,
        isRecentlyExpiredMember,
        expirationDate,
        allocationByLevelId,
        refresh,
      }}
    >
      {children}
    </MembershipContext.Provider>
  );
}

/**
 * @hook useMembership
 * @description Returns membership data and derived status flags fetched at the
 * dashboard layout level. Must be used within a MembershipProvider.
 * @throws If called outside a MembershipProvider.
 */
export function useMembership(): MembershipContextValue {
  const ctx = useContext(MembershipContext);
  if (ctx === null) {
    throw new Error("useMembership must be used within a MembershipProvider");
  }
  return ctx;
}
